import React, { useEffect, useState } from "react";
import { adminApi } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import {
    ChevronDown,
    ChevronRight,
    Send,
    UserCircle2,
    MessageSquare,
    Loader2,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_OPTIONS = ["open", "in_progress", "resolved"];

export default function Tickets() {
    const [items, setItems] = useState([]);
    const [admins, setAdmins] = useState([]);
    const [expanded, setExpanded] = useState({});
    const [commentDraft, setCommentDraft] = useState({});
    const [savingComment, setSavingComment] = useState({});

    const load = () =>
        adminApi.get("/api/admin/tickets").then((r) => setItems(r.data.items));

    useEffect(() => {
        load();
        adminApi.get("/api/admin/admins").then((r) => setAdmins(r.data.items));
    }, []);

    const updateStatus = async (id, status) => {
        await adminApi.patch(`/api/admin/tickets/${id}`, { status });
        toast.success(`Status set to ${status.replace("_", " ")}`);
        load();
    };

    const setAssignee = async (id, email) => {
        await adminApi.put(`/api/admin/tickets/${id}/assignee`, {
            assignee_email: email || null,
        });
        toast.success(email ? "Ticket assigned" : "Assignment cleared");
        load();
    };

    const postComment = async (id) => {
        const body = (commentDraft[id] || "").trim();
        if (!body) return;
        setSavingComment((s) => ({ ...s, [id]: true }));
        try {
            await adminApi.post(`/api/admin/tickets/${id}/comments`, { body });
            setCommentDraft((d) => ({ ...d, [id]: "" }));
            toast.success("Comment added");
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to add comment");
        } finally {
            setSavingComment((s) => ({ ...s, [id]: false }));
        }
    };

    return (
        <div className="p-10 max-w-5xl" data-testid="page-tickets">
            <div className="mb-8">
                <div className="text-xs uppercase tracking-[0.25em] text-stone-500 mb-2">
                    Support
                </div>
                <h1 className="font-heading text-5xl text-orchid-700">Tickets</h1>
                <p className="text-stone-600 mt-2">
                    Concerns raised through the Concierge — assign owners, comment, and respond within SLA.
                </p>
            </div>

            <div className="space-y-2">
                {items.map((t) => {
                    const open = !!expanded[t.id];
                    const f = t.fields || {};
                    const comments = t.comments || [];
                    return (
                        <div
                            key={t.id}
                            data-testid={`ticket-${t.id}`}
                            className="bg-white border border-stone-200 rounded-lg overflow-hidden"
                        >
                            <button
                                onClick={() =>
                                    setExpanded({ ...expanded, [t.id]: !open })
                                }
                                className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-stone-50"
                            >
                                <div className="flex items-center gap-4 min-w-0">
                                    {open ? (
                                        <ChevronDown size={18} className="text-stone-400" />
                                    ) : (
                                        <ChevronRight size={18} className="text-stone-400" />
                                    )}
                                    <div className="min-w-0">
                                        <div className="font-medium text-stone-900 truncate">
                                            #{t.ticket_number} — {f.name || "—"}
                                            <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-stone-100 text-stone-600">
                                                {f.issue || "General"}
                                            </span>
                                        </div>
                                        <div className="text-xs text-stone-500 mt-0.5 flex items-center gap-3 flex-wrap">
                                            <span>{f.mobile} · {f.email}</span>
                                            {t.assignee_name && (
                                                <span className="inline-flex items-center gap-1 text-orchid-700 font-medium">
                                                    <UserCircle2 size={11} /> {t.assignee_name}
                                                </span>
                                            )}
                                            {comments.length > 0 && (
                                                <span className="inline-flex items-center gap-1 text-stone-500">
                                                    <MessageSquare size={11} /> {comments.length}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3 shrink-0">
                                    <span className="text-xs text-stone-500 hidden md:inline">
                                        {formatDateTime(t.created_at)}
                                    </span>
                                    <select
                                        onClick={(e) => e.stopPropagation()}
                                        value={t.status}
                                        onChange={(e) => updateStatus(t.id, e.target.value)}
                                        data-testid={`ticket-status-${t.id}`}
                                        className={`text-xs px-2 py-1 rounded border ${
                                            t.status === "open"
                                                ? "bg-orchid-500/10 border-orchid-500/30 text-orchid-600"
                                                : t.status === "in_progress"
                                                  ? "bg-gold-500/15 border-gold-500/30 text-gold-700"
                                                  : "bg-sage-500/15 border-sage-500/30 text-sage-700"
                                        }`}
                                    >
                                        {STATUS_OPTIONS.map((s) => (
                                            <option key={s} value={s}>
                                                {s.replace("_", " ")}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </button>
                            {open && (
                                <div className="px-5 pb-5 pt-4 border-t border-stone-100 bg-stone-50/40 text-sm space-y-5">
                                    <div>
                                        <div className="text-[10px] uppercase tracking-wider text-stone-500 mb-1">
                                            Description
                                        </div>
                                        <div className="text-stone-900 whitespace-pre-wrap bg-white border border-stone-200 rounded-md p-3">
                                            {f.description || "—"}
                                        </div>
                                    </div>

                                    {/* Assignment */}
                                    <div>
                                        <div className="text-[10px] uppercase tracking-wider text-stone-500 mb-1">
                                            Assignee
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <select
                                                value={t.assignee_email || ""}
                                                onChange={(e) => setAssignee(t.id, e.target.value)}
                                                data-testid={`ticket-assignee-${t.id}`}
                                                className="px-3 py-1.5 rounded-md border border-stone-200 text-xs bg-white"
                                            >
                                                <option value="">— Unassigned —</option>
                                                {admins.map((a) => (
                                                    <option key={a.id} value={a.email}>
                                                        {a.name} · {a.email}
                                                    </option>
                                                ))}
                                            </select>
                                            {t.assigned_at && (
                                                <span className="text-[10px] text-stone-500">
                                                    {formatDateTime(t.assigned_at)}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Comments */}
                                    <div>
                                        <div className="text-[10px] uppercase tracking-wider text-stone-500 mb-2">
                                            Comments ({comments.length})
                                        </div>
                                        <div
                                            className="space-y-2 mb-3"
                                            data-testid={`ticket-comments-${t.id}`}
                                        >
                                            {comments.map((c) => (
                                                <div
                                                    key={c.id}
                                                    className="bg-white border border-stone-200 rounded-md p-3"
                                                >
                                                    <div className="flex items-center justify-between text-[11px] text-stone-500 mb-1">
                                                        <span className="font-medium text-orchid-700">
                                                            {c.author_name || c.author_email}
                                                        </span>
                                                        <span>{formatDateTime(c.created_at)}</span>
                                                    </div>
                                                    <div className="text-stone-800 text-sm whitespace-pre-wrap">
                                                        {c.body}
                                                    </div>
                                                </div>
                                            ))}
                                            {comments.length === 0 && (
                                                <div className="text-xs text-stone-400 italic">
                                                    No comments yet.
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex gap-2">
                                            <textarea
                                                value={commentDraft[t.id] || ""}
                                                onChange={(e) =>
                                                    setCommentDraft((d) => ({
                                                        ...d,
                                                        [t.id]: e.target.value,
                                                    }))
                                                }
                                                rows={2}
                                                placeholder="Record a comment, next step or follow-up note…"
                                                data-testid={`ticket-comment-input-${t.id}`}
                                                className="flex-1 px-3 py-2 rounded-md border border-stone-200 text-sm focus:outline-none focus:border-orchid-700 focus:ring-2 focus:ring-orchid-700/15"
                                            />
                                            <button
                                                onClick={() => postComment(t.id)}
                                                disabled={
                                                    savingComment[t.id] ||
                                                    !(commentDraft[t.id] || "").trim()
                                                }
                                                data-testid={`ticket-comment-submit-${t.id}`}
                                                className="self-end inline-flex items-center gap-1.5 bg-orchid-700 hover:bg-orchid-600 text-white text-xs font-medium px-3 py-2 rounded-md disabled:opacity-50"
                                            >
                                                {savingComment[t.id] ? (
                                                    <Loader2 size={13} className="animate-spin" />
                                                ) : (
                                                    <Send size={13} />
                                                )}
                                                Post
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
                {items.length === 0 && (
                    <div className="text-center text-stone-500 py-16 bg-white border border-stone-200 rounded-lg">
                        No support tickets at the moment.
                    </div>
                )}
            </div>
        </div>
    );
}
